import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, ArrowRight, Home, FileQuestion, BookOpen, Phone, ExternalLink } from 'lucide-react';
import Seo from '../components/Seo';

/**
 * Custom 404. The catch-all route in App.js previously rendered <Home>
 * which was a poor UX (looked like you actually arrived somewhere). This
 * dedicated component admits the page is missing and offers a sensible
 * next action — search the site, browse popular service pages, or talk
 * to a human.
 *
 * Lightweight client-side search filters the same flat link list — no
 * backend round trip, no debounce gymnastics, sub-millisecond.
 */
const POPULAR_LINKS = [
  { to: '/business-voip',             label: 'Cloud Hosted PBX',            kw: 'pbx phone system voip business' },
  { to: '/microsoft-teams',           label: 'Microsoft Teams — TeamMate',  kw: 'teams direct routing microsoft' },
  { to: '/sip-trunk',                 label: 'SIP Trunk Packages',          kw: 'sip trunk channels' },
  { to: '/virtual-numbers',           label: 'Phone Numbers / DID',         kw: 'numbers did toll-free uifn international' },
  { to: '/inbound-contact-centre',    label: 'Inbound Contact Centre',      kw: 'inbound ivr queue contact centre' },
  { to: '/outbound-contact-centre',   label: 'Outbound Contact Centre',     kw: 'outbound dialer sales' },
  { to: '/video-conferencing',        label: 'Video Conferencing',          kw: 'video meeting webinar webrtc' },
  { to: '/web-phone',                 label: 'Web Phone & Softphone',       kw: 'softphone webrtc browser' },
  { to: '/crm-integrations',          label: 'CRM Integrations',            kw: 'crm hubspot salesforce zoho nava' },
  { to: '/unified-communications',    label: 'Unified Communications',      kw: 'uc ucaas chat sms presence' },
  { to: '/phone-features',            label: '50+ Phone Features',          kw: 'features list' },
  { to: '/platform',                  label: 'Platform & Network',          kw: 'platform datacentre redundancy' },
  { to: '/microsoft-calling-plans-vs-teammate', label: 'Calling Plans vs TeamMate', kw: 'comparison cost calculator tco' },
  { to: '/pricing',                   label: 'Pricing',                      kw: 'pricing plans cost' },
  { to: '/about',                     label: 'About VoIPinger',             kw: 'about cli secure company' },
  { to: '/blog',                      label: 'Blog',                         kw: 'blog articles' },
  { to: '/resources',                 label: 'Resources & Guides',          kw: 'resources guides pdf tutorial' },
  { to: '/contact',                   label: 'Contact us',                   kw: 'contact help support' },
];

export default function NotFound() {
  const [q, setQ] = useState('');
  const location = useLocation();

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return POPULAR_LINKS.slice(0, 9);
    return POPULAR_LINKS.filter(
      (l) =>
        l.label.toLowerCase().includes(needle)
        || l.kw.toLowerCase().includes(needle)
        || l.to.toLowerCase().includes(needle),
    );
  }, [q]);

  return (
    <>
      <Seo
        title="Page not found · VoIPinger"
        description="The page you were looking for doesn't exist. Search VoIPinger or browse our popular services."
        path={location.pathname}
      />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden mesh-bg text-white">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-violet-500/15 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -right-32 w-96 h-96 bg-fuchsia-500/15 rounded-full blur-3xl animate-pulse-slow" />
        <div className="relative container-x text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-violet-100/90 text-[11px] font-bold uppercase tracking-widest mb-5">
            <FileQuestion size={11} /> Lost in the cloud
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05]">
            404
            <br />
            <span className="bg-gradient-to-r from-violet-200 via-fuchsia-200 to-violet-300 bg-clip-text text-transparent">
              Page not found
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-violet-50/85 leading-relaxed">
            The page <code className="font-mono text-violet-200/80">{location.pathname}</code> doesn&rsquo;t
            exist on VoIPinger. It may have moved, been renamed or never been here at all.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for a service, feature or topic..."
                autoFocus
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none text-base"
              />
            </div>

            {matches.length === 0 ? (
              <p className="text-center text-slate-500 mt-10">
                No matches. <Link to="/contact" className="text-violet-700 underline">Tell us what you were looking for</Link>.
              </p>
            ) : (
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {matches.map((m) => (
                  <Link
                    key={m.to}
                    to={m.to}
                    className="group flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
                  >
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{m.label}</p>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">{m.to}</p>
                    </div>
                    <ArrowRight size={14} className="text-slate-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition" />
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold shadow-lg hover:shadow-xl transition-shadow">
                <Home size={14} /> Back to home
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-700 border border-slate-200 text-sm font-bold hover:border-violet-300 transition-colors">
                <Phone size={14} /> Talk to a specialist
              </Link>
              <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-700 border border-slate-200 text-sm font-bold hover:border-violet-300 transition-colors">
                <BookOpen size={14} /> Read the blog
              </Link>
            </div>

            <p className="text-xs text-slate-400 text-center mt-10">
              Found a broken link?{' '}
              <a href="mailto:info@voipinger.com?subject=Broken%20link%20on%20voipinger.com" className="hover:text-violet-600 underline">
                Let us know <ExternalLink size={11} className="inline -mt-0.5" />
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
